import { DragEvent, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-hot-toast";
import { CloudArrowUpIcon, ArrowPathIcon, TrashIcon, EyeSlashIcon, DocumentIcon, PhotoIcon, TableCellsIcon, PhoneIcon, ChevronDownIcon, InformationCircleIcon, ShieldExclamationIcon, XMarkIcon, VideoCameraIcon } from "@heroicons/react/24/outline";
import cn from "classnames";
import Button from "../../common/Button";
import Reveal, { revealProps } from "../../common/Reveal";
import { api, FileListItem, InvestigationDetail, UsageSummary, usageStanding } from "../api";
import { ACCEPT, useUploader } from "../InvestigationUpload";
import ImportModal from "../ImportModal";
import { useStaffView } from "../StaffView";

const KIND_ICON: Record<string, React.FC<{ className?: string }>> = { jail_call: PhoneIcon, document: DocumentIcon, spreadsheet: TableCellsIcon, photo: PhotoIcon, video: VideoCameraIcon };

const FILE_KINDS = [
  {
    icon: PhoneIcon,
    title: "Jail calls",
    formats: "WAV · MP3 · M4A · FLAC",
    body: "Each call is transcribed with speaker separation, translated to English when needed, then read in full by the AI. You get a relevance score, flagged moments with timestamps, the people mentioned, and a glossary of slang or coded language.",
    tip: "Drop the whole export from Securus, ViaPath/GTL, or ICSolutions. Dates, inmate IDs, and dialed numbers are read from the filenames.",
  },
  {
    icon: DocumentIcon,
    title: "Documents",
    formats: "PDF (scanned or native)",
    body: "Reports, discovery, letters, and statements are OCR'd and indexed page by page, so the chat can quote them with a page citation.",
    tip: "Scanned PDFs work; text is read from the image.",
  },
  {
    icon: TableCellsIcon,
    title: "Spreadsheets",
    formats: "XLSX · XLS · CSV",
    body: "Bank statements, phone logs, and commissary records become a queryable table, and patterns worth acting on — transfers to known names, activity around key dates, structuring — are flagged as leads. Ask for totals, date ranges, or everything to a name and the answer is computed, not guessed.",
    tip: "Keep the header row; date, amount, and payee columns are detected automatically.",
  },
  {
    icon: VideoCameraIcon,
    title: "Video",
    formats: "MP4 · MOV · WEBM · MKV",
    body: "Body cam, surveillance, cell-phone, and dash cam footage is transcribed like a call and also watched: the footage is read frame by frame where something happens, so leads anchor to the second and the still that shows it, plates and signs are read, and the chat can answer what was visible, not just what was said.",
    tip: "Long, quiet surveillance files are fine; idle stretches cost almost nothing. Add a \"watch for\" note on the file to have the AI look for a vehicle or a person.",
  },
  {
    icon: PhotoIcon,
    title: "Photos",
    formats: "JPG · PNG · WEBP",
    body: "Each photo is read like a document: described in full, visible text transcribed, and anything worth acting on — a threat in a screenshot, a plate, cash, a weapon — flagged as a lead, with names, dates, and coded language carried into the investigation.",
    tip: "Screenshots of messages and photographed notes or letters work especially well here.",
  },
];

const FileKindGuide: React.FC<{ compact?: boolean }> = ({ compact }) => (
  <div className={cn("grid gap-3", compact ? "sm:grid-cols-2 lg:grid-cols-4" : "sm:grid-cols-2")}>
    {FILE_KINDS.map((k) => (
      <div key={k.title} className="rounded-xl border border-base-300 bg-base-100 p-4">
        <div className="flex items-center gap-2">
          <span className="size-8 rounded-lg bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300 flex items-center justify-center">
            <k.icon className="size-4" />
          </span>
          <div className="min-w-0">
            <p className="font-semibold text-base-content leading-tight">{k.title}</p>
            <p className="text-[11px] text-base-content/50">{k.formats}</p>
          </div>
        </div>
        {!compact && (
          <>
            <p className="mt-2.5 text-sm leading-5 text-base-content/75">{k.body}</p>
            <p className="mt-2 text-[11px] leading-4 text-base-content/50">{k.tip}</p>
          </>
        )}
      </div>
    ))}
  </div>
);

const FilesTab: React.FC<{ investigationId: string; detail: InvestigationDetail; onRefresh: () => Promise<void>; onListChanged: () => Promise<void> }> = ({ investigationId, detail, onRefresh, onListChanged }) => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { enqueue, items, active } = useUploader();
  const [files, setFiles] = useState<FileListItem[]>([]);
  const [usage, setUsage] = useState<UsageSummary | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [attesting, setAttesting] = useState<File[] | null>(null);
  const [attestChecked, setAttestChecked] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [deleting, setDeleting] = useState<FileListItem | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  // Staff portal view: read-only, and the usage banner would be the staff account's own.
  const staffView = Boolean(useStaffView());

  const load = async () => {
    setFiles(await api.files(investigationId));
  };
  useEffect(() => {
    load();
    if (!staffView) api.usage().then(setUsage).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [investigationId]);
  // Reload as soon as an upload lands (or the batch finishes) so new rows appear
  // without waiting for the poll below or a page refresh.
  const doneCount = items.filter((i) => i.status === "done").length;
  useEffect(() => {
    if (doneCount > 0 || active) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doneCount, active]);
  useEffect(() => {
    const anyActive = files.some((f) => ["pending", "uploading", "processing"].includes(f.processing_status || "")) || active;
    if (!anyActive) return;
    const t = setInterval(load, 6000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files, active, items.length]);

  // Legacy deep links (chat citations and Brief chips used to emit
  // ?file=<id>): forward them to the file detail route.
  useEffect(() => {
    const fileId = params.get("file");
    if (!fileId) return;
    navigate(`/investigations/${investigationId}/files/${fileId}`, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const startUpload = async (selected: File[]) => {
    if (!selected.length) return;
    if (!detail.privilege_attested_at) {
      setAttesting(selected);
      return;
    }
    await enqueue(investigationId, selected);
  };

  const cancelAttestation = () => {
    setAttesting(null);
    setAttestChecked(false);
  };

  const confirmAttestation = async () => {
    try {
      await api.attest(investigationId);
      await onRefresh();
      const pending = attesting || [];
      setAttesting(null);
      setAttestChecked(false);
      await enqueue(investigationId, pending);
    } catch {
      toast.error("Could not record attestation");
    }
  };

  // Which file rows have already been shown, so only newcomers animate in.
  const seenRows = useRef<Set<string>>(new Set());
  useEffect(() => {
    for (const f of files) seenRows.current.add(f.id);
  }, [files]);
  let newRowSlot = 0;

  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    startUpload(Array.from(e.dataTransfer.files));
  };

  const openFile = async (f: FileListItem) => {
    if (f.kind === "jail_call") {
      const calls = await api.calls(investigationId);
      const c = calls.find((x) => x.file_id === f.id);
      if (c) navigate(`/investigations/${investigationId}/calls/${c.id}`);
      return;
    }
    navigate(`/investigations/${investigationId}/files/${f.id}`);
  };

  // No running counter, ever: a number makes detectives ration themselves,
  // which is exactly what the shared pool is meant to stop. The banner only
  // appears when the allowance is nearly or fully used.
  const standing = usage ? usageStanding(usage) : null;
  const nearAllowance = standing != null && standing.pct >= 80;
  const span = standing?.period.interval === "year" ? "billing year" : "month";
  const howMuch = standing && standing.pct >= 100 ? "all" : "most";
  const hoursLine = standing
    ? usage?.scope === "pool"
      ? `Your department has used ${howMuch} of its Investigations hours for this ${span}.`
      : `You've used ${howMuch} of your Investigations hours for this ${span}.`
    : "";
  const liftHint =
    usage?.scope === "pool"
      ? " Ask your agency administrator to add hours or seats."
      : ` Uploads return to full speed when the next ${span} starts.`;

  return (
    <div className="max-w-6xl">
      {usage && (usage.flagLevel !== "none" || nearAllowance) && (
        <div className={cn("mb-4 rounded-lg px-4 py-2 text-sm", usage.flagLevel === "throttled" || usage.flagLevel === "suspended" ? "bg-red-50 dark:bg-red-950/40 text-red-800 dark:text-red-400" : "bg-amber-50 dark:bg-amber-950/40 text-amber-800 dark:text-amber-400")}>
          {usage.flagLevel === "throttled" ? "Uploads on this account are rate-limited. " : usage.flagLevel === "suspended" ? "Uploads on this account are paused. " : ""}
          {usage.flagReason ? `${usage.flagReason}. ` : ""}
          {hoursLine}
          {usage.flagLevel === "throttled" && liftHint}
        </div>
      )}
      {files.length === 0 && !active && (
        <div className="mb-5">
          <h2 className="text-xl font-bold text-base-content">Start by adding the evidence</h2>
          <p className="mt-1 text-sm text-base-content/70 max-w-2xl">
            Everything else in this investigation is built from what you upload here. Jail calls are the heart of it; documents, spreadsheets, and photos give the AI the context to connect them.
          </p>
        </div>
      )}
      {!staffView && (
      <>
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        className={cn("rounded-xl border-2 border-dashed p-8 text-center cursor-pointer transition-colors mb-6", dragOver ? "border-blue-500 bg-blue-50 dark:bg-blue-900/40" : "border-base-300 hover:border-blue-200 dark:hover:border-blue-800 bg-base-100")}
      >
        <CloudArrowUpIcon className="size-8 mx-auto text-base-content/40 mb-2" />
        <p className="font-medium text-base-content">Drop jail calls, documents, spreadsheets, or photos here</p>
        <p className="text-xs text-base-content/60 mt-1">
          Up to 100 files at a time. Audio: WAV, MP3, M4A, FLAC, AAC, OGG, AMR, or MP4 video. Documents: PDF, Word (DOC, DOCX), TXT. Spreadsheets: XLSX, XLS, CSV, TSV. Photos: JPG, PNG, WEBP. Duplicates are skipped automatically.
        </p>
        <input ref={inputRef} type="file" multiple accept={ACCEPT} className="hidden" onChange={(e) => { startUpload(Array.from(e.target.files || [])); e.target.value = ""; }} />
      </div>
      <div className="-mt-3 mb-6 text-center">
        <button
          type="button"
          onClick={() => setImportOpen(true)}
          className="text-xs text-blue-600 dark:text-blue-300 hover:underline"
        >
          or import from your cases — previous uploads, your reports, bodycam
        </button>
      </div>
      </>
      )}

      {files.length === 0 && !active && (
        <div className="mb-6">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-base-content/50 mb-3">What you can upload</p>
          <FileKindGuide />
          <ol className="mt-4 grid sm:grid-cols-3 gap-3 text-sm">
            {[
              ["1", "Upload", "Drop up to 100 files at once. Duplicates are skipped. Once they're in, you can close the tab."],
              ["2", "We analyze", "Transcription, translation, and a full read of every call. The first ten finish in a few minutes; a batch of fifty in under an hour."],
              ["3", "You review", "Calls ranked by relevance, leads with a play button on the exact moment, a written brief, and a chat that cites its sources."],
            ].map(([n, t, b]) => (
              <li key={n} className="flex gap-3 rounded-xl bg-base-200/60 p-3">
                <span className="size-6 shrink-0 rounded-full bg-blue-500 text-white text-xs font-bold flex items-center justify-center">{n}</span>
                <div>
                  <p className="font-semibold text-base-content">{t}</p>
                  <p className="text-xs leading-4 text-base-content/60">{b}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      )}
      {files.length > 0 && (
        <div className="mb-4">
          <button onClick={() => setGuideOpen((o) => !o)} className="inline-flex items-center gap-1.5 text-xs text-base-content/60 hover:text-base-content">
            <InformationCircleIcon className="size-4" /> What can I upload, and what happens to it?
            <ChevronDownIcon className={cn("size-3.5 transition-transform", guideOpen && "rotate-180")} />
          </button>
          {guideOpen && (
            <div className="mt-3">
              <FileKindGuide compact />
            </div>
          )}
        </div>
      )}
      {files.length === 0 ? null : (
        <Reveal className="overflow-x-auto rounded-xl border border-base-300">
          <table className="table table-sm">
            <thead>
              <tr>
                <th>File</th>
                <th>Type</th>
                <th>Size</th>
                <th>Status</th>
                <th>Added</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {files.map((f) => {
                const Icon = KIND_ICON[f.kind] || DocumentIcon;
                const status = f.privileged_at ? "privileged" : f.processing_status || "pending";
                // Rows that arrived in this render (first load, or a later
                // upload landing) stagger in from slot 0; rows already on
                // screen never re-animate.
                const enter = seenRows.current.has(f.id) ? null : revealProps(newRowSlot++);
                return (
                  <tr key={f.id} style={enter?.style} className={cn("hover", enter?.className)}>
                    <td className="max-w-md">
                      <button className="flex items-center gap-2 text-left hover:underline" onClick={() => openFile(f)}>
                        <Icon className="size-4 text-base-content/50 shrink-0" />
                        <span className="truncate">{f.original_file_name}</span>
                      </button>
                    </td>
                    <td className="text-xs text-base-content/70">{f.kind.replace("_", " ")}</td>
                    <td className="text-xs text-base-content/70">{f.file_size}</td>
                    <td className="text-xs">
                      {status === "failed" ? (
                        <span className="text-red-600 dark:text-red-400" title={f.error_message || ""}>Failed{f.error_message ? `: ${f.error_message.slice(0, 60)}` : ""}</span>
                      ) : status === "completed" ? (
                        <span className="text-green-700 dark:text-green-400">Ready</span>
                      ) : status === "privileged" ? (
                        <span className="text-base-content/60">Privileged</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-amber-700 dark:text-amber-400"><span className="size-2 rounded-full bg-amber-500 animate-pulse mt-0.5" />{f.activeJob?.stage ? f.activeJob.stage.replace("_", " ") : status}</span>
                      )}
                    </td>
                    <td className="text-xs text-base-content/60 whitespace-nowrap">{f.created_at ? new Date(f.created_at).toLocaleDateString() : ""}</td>
                    <td className="whitespace-nowrap">
                      {!staffView && status === "failed" && !f.privileged_at && (
                        <Button variant="clean" size="xs" className="px-1.5 hover:bg-base-200" title="Retry" onClick={async () => { await api.retryFile(investigationId, f.id); toast.success("Retrying"); load(); }}>
                          <ArrowPathIcon className="size-4" />
                        </Button>
                      )}
                      {!staffView && !f.privileged_at && (
                        <Button
                          variant="clean"
                          size="xs"
                          className="px-1.5 hover:bg-base-200"
                          title="Mark privileged (removes AI analysis)"
                          onClick={async () => {
                            if (!window.confirm("Mark this file as attorney-client privileged? Its transcript index and AI findings will be removed and it will be excluded from search and chat.")) return;
                            await api.markPrivileged(investigationId, f.id);
                            toast.success("Marked privileged");
                            load();
                            onRefresh();
                          }}
                        >
                          <EyeSlashIcon className="size-4" />
                        </Button>
                      )}
                      {detail.role === "owner" && !staffView && (
                        <Button
                          variant="clean"
                          size="xs"
                          className="px-1.5 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/40"
                          title="Delete"
                          onClick={() => setDeleting(f)}
                        >
                          <TrashIcon className="size-4" />
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Reveal>
      )}

      {deleting && (
        <div className="modal modal-open">
          <div className="modal-box max-w-md">
            <h3 className="font-semibold text-base-content">Delete "{deleting.original_file_name}"?</h3>
            <p className="mt-2 text-sm text-base-content/70">
              This permanently removes the file
              {deleting.processing_status === "completed"
                ? " and everything the analysis found in it: its flags, transcript or extracted text, and people mentions. This cannot be undone."
                : ". This cannot be undone."}
            </p>
            {deleting.processing_status === "completed" && (
              <p className="mt-2 text-sm text-base-content/70">
                The investigation brief was written with this file included. You can update it now, or keep it — the Brief tab
                will note that this file was deleted after it was written.
              </p>
            )}
            <div className="modal-action flex-wrap gap-2">
              <Button variant="secondary" size="sm" disabled={deleteBusy} onClick={() => setDeleting(null)}>
                Cancel
              </Button>
              {deleting.processing_status === "completed" ? (
                <>
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={deleteBusy}
                    className="text-red-600 dark:text-red-400"
                    onClick={async () => {
                      setDeleteBusy(true);
                      try {
                        await api.deleteFile(investigationId, deleting.id);
                        toast.success("File deleted");
                        setDeleting(null);
                        load();
                        onRefresh();
                        onListChanged();
                      } catch {
                        toast.error("Failed to delete");
                      } finally {
                        setDeleteBusy(false);
                      }
                    }}
                  >
                    Delete only
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    disabled={deleteBusy}
                    onClick={async () => {
                      setDeleteBusy(true);
                      try {
                        await api.deleteFile(investigationId, deleting.id, { rollup: true });
                        toast.success("File deleted — updating the brief");
                        setDeleting(null);
                        load();
                        onRefresh();
                        onListChanged();
                      } catch {
                        toast.error("Failed to delete");
                      } finally {
                        setDeleteBusy(false);
                      }
                    }}
                  >
                    Delete & update brief
                  </Button>
                </>
              ) : (
                <Button
                  variant="primary"
                  size="sm"
                  disabled={deleteBusy}
                  className="bg-red-600 hover:bg-red-700 border-red-600 hover:border-red-700"
                  onClick={async () => {
                    setDeleteBusy(true);
                    try {
                      await api.deleteFile(investigationId, deleting.id);
                      toast.success("File deleted");
                      setDeleting(null);
                      load();
                      onRefresh();
                      onListChanged();
                    } catch {
                      toast.error("Failed to delete");
                    } finally {
                      setDeleteBusy(false);
                    }
                  }}
                >
                  Delete
                </Button>
              )}
            </div>
          </div>
          <div className="modal-backdrop" onClick={() => !deleteBusy && setDeleting(null)} />
        </div>
      )}

      <ImportModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        investigationId={investigationId}
        attested={Boolean(detail.privilege_attested_at)}
        onAttest={async () => {
          await api.attest(investigationId);
          await onRefresh();
        }}
        onImported={() => {
          load();
          onRefresh();
        }}
      />

      {attesting && (
        <dialog open className="modal modal-open">
          <div className="modal-box max-w-md p-0 overflow-hidden rounded-2xl">
            <div className="flex items-start gap-3 px-6 pt-6 pb-4">
              <div className="size-10 shrink-0 rounded-xl bg-amber-50 dark:bg-amber-950/40 text-amber-600 dark:text-amber-400 flex items-center justify-center">
                <ShieldExclamationIcon className="size-6" />
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="text-lg font-bold text-base-content leading-tight">Before you upload</h3>
                <p className="text-sm text-base-content/60 mt-0.5">
                  Attorney-client communications are privileged. Uploading one to an AI system can taint the investigation.
                </p>
              </div>
              <button type="button" onClick={cancelAttestation} className="p-1 rounded-md text-base-content/50 hover:bg-base-200 hover:text-base-content" title="Close">
                <XMarkIcon className="size-5" />
              </button>
            </div>
            <label className={cn("mx-6 mb-3 flex items-start gap-3 rounded-xl border px-4 py-3 text-sm cursor-pointer transition-colors", attestChecked ? "border-blue-300 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/40" : "border-base-300 bg-base-200/50 hover:bg-base-200")}>
              <input type="checkbox" className="checkbox checkbox-sm mt-0.5" checked={attestChecked} onChange={(e) => setAttestChecked(e.target.checked)} />
              <span className="text-base-content/90 leading-6">I certify that the recordings I upload to this investigation are not attorney-client privileged communications, and I will mark any file privileged if I later learn otherwise.</span>
            </label>
            <p className="mx-6 mb-5 text-[11px] text-base-content/50">This attestation is recorded with your name and the time, once per investigation.</p>
            <div className="px-6 pb-6 flex items-center justify-end gap-2">
              <Button variant="secondary" size="sm" onClick={cancelAttestation}>
                Cancel
              </Button>
              <Button variant="primary" size="sm" disabled={!attestChecked} onClick={confirmAttestation}>
                Continue with {attesting.length} file{attesting.length === 1 ? "" : "s"}
              </Button>
            </div>
          </div>
          <button type="button" className="modal-backdrop" onClick={cancelAttestation} aria-label="Close" />
        </dialog>
      )}
    </div>
  );
};

export default FilesTab;
